import React, { useEffect } from 'react';
import { Col, Form, Modal, Row } from 'antd';
import { useSelector } from 'react-redux';
import NameField from './NameField';
import UploadField from '../../UploadField/UploadField.jsx';
import DescriptionField from './DescriptionField.jsx';
import AvailableForClientField from './AvailableForClientField.jsx.jsx';
import FromDateField from './FromDateField.jsx';
import { fieldNames } from './constants.js';
import ToDateField from './ToDateField.jsx';
import { fromDateStr, toDateStr } from '../../../../../../helpers/utcDate.js';
import useDocChangeMethods from '../../../../hooks/useChangeDocModalVisible.js';
import {
  useAddDocMutation,
  useChangeDocMutation,
} from '../../../../mutations/useDocMutations.js';
import { createFileSizeValidator } from '../../../../utils/fileSizeValidator.js';
import { useChangeAddressAcceptance } from '../../../../hooks/useChangeAddressAccpetance.js';
import useFlatsGridState from '../../../../hooks/useFlatsGridState.js';

const prepareValues = (values, editMode = false) => {
  return Object.entries(values).reduce((acc, [key, value]) => {
    let val = typeof value === 'string' ? value.trim() : value;

    const needSetVal =
      val !== null &&
      val !== undefined &&
      (typeof val !== 'string' || val !== '');

    if (needSetVal) {
      acc[key] =
        key === fieldNames.fromDate
          ? editMode
            ? fromDateStr(val)
            : val.format('YYYY-MM-DD')
          : key === fieldNames.toDate
          ? editMode
            ? toDateStr(val)
            : val.format('YYYY-MM-DD')
          : val;
    }

    return acc;
  }, {});
};

const maxFileSizeMb = 50;
const fileSizeValidator = createFileSizeValidator(
  maxFileSizeMb,
  `Размер файла превышает ${maxFileSizeMb} Мб`
);
const DocModal = ({ form }) => {
  const { getFieldValue, validateFields, setFieldsValue } = form;

  const { flatsGridQueryKey } = useFlatsGridState();

  const { docFormData, currentFlat } = useSelector(
    (state) => state.housingStockStore
  );

  const addressId = currentFlat?.addressId;

  const { entity, params, attachment, typeId } = docFormData || {};

  const { closeModal } = useDocChangeMethods();

  const { mutate: addDoc, isLoading: isAddDocLoading } = useAddDocMutation({
    entity,
    params,
    typeId,
  });

  const { mutate: changeDoc, isLoading: isChangeDocLoading } =
    useChangeDocMutation({
      entity,
      params,
    });

  const { chengeAsync: chengeAcceptance, loading: loadingAcceptanceChange } =
    useChangeAddressAcceptance();

  const filesValue = getFieldValue(fieldNames.files);
  const nameValue = getFieldValue(fieldNames.name);

  const availableForClient =
    getFieldValue(fieldNames.availableToClient) ??
    attachment?.availableForClient ??
    true;

  const handleSubmit = (e) => {
    e.preventDefault();

    validateFields(async (err, values) => {
      if (err) {
        return;
      }

      const { files, ...rawData } = values;
      const data = prepareValues(rawData, Boolean(attachment));

      attachment
        ? await changeDoc({
            data,
            attachId: attachment.id,
          })
        : await addDoc({ files, data });

      if (typeId === 2 ?? !attachment) {
        await chengeAcceptance({
          flatId: addressId,
          AcceptanceStatusId: 4,
          flatsGridQueryKey,
        });
      }

      closeModal();
    });
  };

  useEffect(() => {
    if (filesValue?.length === 1 && !nameValue) {
      const parts = filesValue?.[0]?.name?.split('.');
      const name = parts?.slice(0, -1).join('.');

      setFieldsValue({ [fieldNames.name]: name });
    }

    if (filesValue?.length > 1) {
      setFieldsValue({ [fieldNames.name]: '' });
      setFieldsValue({ [fieldNames.description]: '' });
    }
  }, [setFieldsValue, filesValue]);

  return (
    <Modal
      title={attachment ? 'Изменить данные документа' : 'Добавить документ'}
      visible={Boolean(docFormData)}
      onOk={handleSubmit}
      onCancel={
        isAddDocLoading || isChangeDocLoading || loadingAcceptanceChange
          ? null
          : closeModal
      }
      okText={attachment ? 'Изменить' : 'Добавить'}
      confirmLoading={
        isAddDocLoading || isChangeDocLoading || loadingAcceptanceChange
      }
      destroyOnClose
    >
      <Form>
        <NameField
          form={form}
          initialValue={attachment?.nameWithoutExt}
          disabled={filesValue?.length > 1}
        />

        {!attachment && (
          <UploadField
            form={form}
            name={fieldNames.files}
            label="Вложения"
            uploadProps={{ multiple: true }}
            rules={[
              {
                validator: fileSizeValidator,
              },
              {
                required: true,
                message: 'Прикрепите файлы',
              },
            ]}
          />
        )}

        <DescriptionField
          form={form}
          initialValue={attachment?.description}
          disabled={filesValue?.length > 1}
        />

        <AvailableForClientField
          form={form}
          initialValue={attachment?.availableForClient}
        />

        {availableForClient && (
          <Row gutter={12}>
            <Col span={12}>
              <FromDateField
                form={form}
                initialValue={attachment?.fromDate}
              />
            </Col>
            <Col span={12}>
              <ToDateField
                form={form}
                initialValue={attachment?.toDate}
              />
            </Col>
          </Row>
        )}
      </Form>
    </Modal>
  );
};

const DocFormModal = Form.create({ name: 'doc_form_modal' })(DocModal);

export default DocFormModal;
